import React, { useEffect } from "react";
import { Routes, Route, Navigate, useSearchParams, useNavigate } from "react-router-dom";

import Login from "./components/Login";
import SessionCreationLobby from "./components/SessionCreationLobby";
import AvatarStreamer from "./components/AvatarStreamer";
import SessionEnded from "./components/SessionEnded";
import ManualConnectionWrapper from "./components/ManualConnectionWrapper";
import Logout from "./components/Logout";
import RequireUser from "./auth/RequireUser";
import RequireSession from "./session/RequireSession";
import { useFlagQuerySync } from "./utils/flagBootstrap";
import { getCachedSupportedCodecsWithHardware } from "./hooks/useDecoderCapabilities";
import config from "./utils/config";

import LandingScreen from "./components/LandingScreen";
import LoginUniversal from "./components/LoginUniversal";
import SessionEndedUniversal from "./components/SessionEndedUniversal";
import PartnerEntry from "./components/PartnerEntry";
import SubClientEntry from "./components/SubClientEntry";
import StatusBanner from "./components/StatusBanner";

// import UserOverlayOnly from "./components/UserOverlayOnly";
// import { LoadingUserOnlyComponent } from "./components/entryflow/LoadingUser";
// import { OnboardingUserOnlyComponent } from "./components/entryflow/OnboardingUser";
// import { MicrophonePermissioningOnlyComponent } from "./components/entryflow/MicrophonePermissioningView";
// import { LoadingSessionOnlyComponent } from "./components/entryflow/LoadingSession";
// import { HeroCardOnlyComponent } from "./components/layout/HeroCardLayout";
// import { FinalizeCreateUserOnlyComponent } from "./components/entryflow/FinalizeCreateUserView";
// import { UserTerminalOnlyComponent } from "./components/entryflow/UserTerminal";
// import { LoadingSessionErrorOnlyComponent } from "./components/entryflow/LoadingSessionError";
// import DegradedHealthOverlay from "./components/utils/DegradedHealthOverlay";
// import { SelectorStartViewOnlyComponent } from "./components/entryflow/SelectorStartView";
// import { SelectorSelectViewOnlyComponent } from "./components/entryflow/SelectorSelectView";
// import { UserWidgetOnly } from "./components/utils/UserWidgetView";
// import { GuiPopupBubbleStackViewOnly } from "./components/hud/GuiPopupBubbleView";
// import GuiPopupAnimationViewOnly from "./components/hud/GuiPopupAnimationViewOnly";
// import { PatientIntakeViewOnly } from "./components/entryflow/PatientIntakeView";
// import PartnerPostPage from "./components/devonly/PartnerPostPage";


function RootPageDecider() {
  const [params] = useSearchParams();
  const token = params.get("join_token");
  return token ? <Navigate to={`/tokenlogin?${params.toString()}`} replace /> : <LandingScreen />;
}

const App: React.FC = () => {

  useFlagQuerySync();
  const navigate = useNavigate();

  // Warm this cache...
  getCachedSupportedCodecsWithHardware();

  // Listen for partner-authenticated events dispatched by nativeJwtExchange.ts after a successful
  // JWT exchange. This is how we get back into React context to navigate to the partner entry point.
  useEffect(() => {
    if (!config.enableNativeBridge) {
      return;
    }

    const handlePartnerAuthenticated = (event: Event) => {
      const customEvent = event as CustomEvent<{ path?: string }>;
      const targetPath = customEvent.detail?.path;
      if (!targetPath) {
        console.warn("[App] partner-authenticated event missing path detail", customEvent.detail);
        return;
      }
      console.log("[App] about to navigate to partner-authenticated path:", targetPath);
      navigate(targetPath, { replace: true });
    };

    window.addEventListener("partner-authenticated", handlePartnerAuthenticated);
    return () => {
      window.removeEventListener("partner-authenticated", handlePartnerAuthenticated);
    };
  }, [navigate]);
  
  return (
    <div className="container">
      <StatusBanner />
      <Routes>
        {/* Only routes are used to debug the layouts/colors/etc. To be removed in prod. */}
        {/*
        <Route path="/useroverlayonly" element={<UserOverlayOnly />} />
        <Route path="/herocardonly" element={<HeroCardOnlyComponent />} />
        <Route path="/loadinguseronly" element={<LoadingUserOnlyComponent />} />
        <Route path="/onboardinguseronly" element={<OnboardingUserOnlyComponent />} />
        <Route path="/microphonepermsonly" element={<MicrophonePermissioningOnlyComponent />} />
        <Route path="/loadingsessiononly" element={<LoadingSessionOnlyComponent />} />
        <Route path="/loadingsessionerroronly" element={<LoadingSessionErrorOnlyComponent />} />
        <Route path="/finalizecreateuseronly" element={<FinalizeCreateUserOnlyComponent />} />
        <Route path="/userterminalonly" element={<UserTerminalOnlyComponent />} />
        <Route path="/selectorstartonly" element={<SelectorStartViewOnlyComponent />} />
        <Route path="/selectorselectonly" element={<SelectorSelectViewOnlyComponent />} />
        <Route path="/userwidgetonly" element={<UserWidgetOnly />} />
        <Route path="/guipopuponly" element={<GuiPopupBubbleStackViewOnly />} />
        <Route path="/guipopupanimationonly" element={<GuiPopupAnimationViewOnly />} />
        <Route path="/patientintakeonly" element={<PatientIntakeViewOnly />} />
        <Route path="/partnerpostpage" element={<PartnerPostPage />} />
        <Route
          path="/degradedhealthonly"
          element={<DegradedHealthOverlay />}
        /> */}

        <Route path="/" element={<RootPageDecider />} />
        <Route path="/devlogin" element={<Login />} />
        <Route path="/tokenlogin" element={<Login />} />
        <Route path="/login" element={<LoginUniversal />} />
        <Route path="/partner" element={<PartnerEntry />} />
        <Route element={<RequireUser />}>
          <Route path="/manual-connection" element={<ManualConnectionWrapper />} />
          <Route path="/universal-session-lobby" element={<SessionCreationLobby />} />
          <Route path="/subcliententry" element={<SubClientEntry />} />
          <Route element={<RequireSession />}>
            <Route path="/avatarstreamer" element={<AvatarStreamer />} />
          </Route>
          <Route path="/investor-session-end" element={<SessionEnded />} />
        </Route>
        <Route path="/universal-session-end" element={<SessionEndedUniversal />} />
        <Route path="/logout" element={<Logout />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
    </div>
  );
};

export const NotFound = () => {
  return (
    <div
      style={{
        backgroundColor: "black",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        height: "100vh",
      }}
    >
      <h2>404 - Not Found</h2>
      <p>The page you're looking for does not exist.</p>
    </div>
  );
};

export default App;
